import { memo, useEffect, useRef, useState } from 'react';
import { Download, Eye, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { downloadWallFile, fileSize } from './fileActions';
import { WallFileIcon } from './WallFileIcon';
import { wallAttachmentsAPI } from '@/lib/api';

// Icônes immédiates ; aucun fichier ni miniature ne se charge avant un clic.
export const WallAttachmentList = memo(function WallAttachmentList({ prospectId, noteId, files = [], onOpen, client = wallAttachmentsAPI }) {
    const [downloading, setDownloading] = useState(null);
    const [error, setError] = useState(null);
    const controller = useRef(null);
    useEffect(() => () => controller.current?.abort(), []);
    const download = async (file) => {
        if (downloading) return;
        const abort = new AbortController(); controller.current = abort;
        setDownloading(file.id); setError(null);
        try { await downloadWallFile(prospectId, file, abort.signal, client); }
        catch { if (!abort.signal.aborted) setError('Téléchargement impossible. Vérifiez votre accès puis réessayez.'); }
        finally { if (!abort.signal.aborted) setDownloading(null); }
    };
    if (!files.length) return null;
    return <div className="mt-2 max-w-md space-y-1.5" data-testid={`wall-files-${noteId}`}>
        {files.map(file => {
            const image = file.kind === 'image';
            const format = file.filename.split('.').pop().toUpperCase();
            return <div key={file.id} className="flex min-w-0 items-center gap-1 rounded-md border border-slate-200/70 bg-slate-50/60 px-2 py-1.5 transition-colors hover:border-slate-300 hover:bg-slate-50" data-testid={`wall-file-${file.id}`}>
                <button type="button" onClick={() => onOpen(file)} className="flex min-w-0 flex-1 items-center gap-2 text-left" title={`Voir ${file.filename}`} aria-label={`Voir ${file.filename}`} data-testid={image ? `wall-file-image-open-${file.id}` : `wall-file-name-open-${file.id}`}>
                    <WallFileIcon file={file} />
                    <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-medium text-slate-600" data-testid={`wall-file-name-${file.id}`}>{file.filename}</p>
                        <p className="mt-0.5 text-[10px] text-slate-400" data-testid={`wall-file-size-${file.id}`}>{format} · {fileSize(file.size)}</p>
                    </div>
                </button>
                <div className="flex shrink-0 items-center">
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-slate-700" onClick={() => onOpen(file)} title="Voir" aria-label={`Voir ${file.filename}`} data-testid={`wall-file-preview-${file.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-slate-700" onClick={() => download(file)} disabled={!!downloading} title="Télécharger" aria-label={`Télécharger ${file.filename}`} data-testid={`wall-file-download-${file.id}`}>
                        {downloading === file.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                    </Button>
                </div>
            </div>;
        })}
        {error && <p role="alert" className="text-xs text-red-700" data-testid={`wall-file-error-${files[0].id}`}>{error}</p>}
    </div>;
});